// ── BottomNav — нижняя панель на телефоне (как в Duolingo, Anki, Spotify) ─────
// Как на макете: Главная · Повторение · Чтение · Статистика · Новый. «Новый»
// открывает лист «создать» (аудио, текст, книга, папка). Аккаунт, тема и
// синхронизация — в меню аккаунта в шапке (Chrome.js → TopBar).
// Высота панели публикуется в CSS-переменной --ac-nav-h: мини-плеер встаёт над ней,
// а списки получают отступ снизу. Слой 90: выше страницы (шапки — 20), ниже любых окон (100+).

import { useEffect, useLayoutEffect, useRef } from 'react';
import { SANS } from './themes';
import Icon from './Icon';

import DueCount, { dueLabel } from './ui/DueCount';
import { t } from './i18n';
import FitText from './ui/FitText';
export const NAV_H = 64;

// Плашка с числами стоит у правого верхнего угла значка, но не выходит за свою
// вкладку: «70 | 10» на узком телефоне шире половины вкладки и раньше заезжала
// на «Статистику». Если не помещается — сдвигается влево ровно на сколько нужно.
function useKeepInside(outerRef, innerRef, deps) {
  useLayoutEffect(() => {
    const outer = outerRef.current, inner = innerRef.current;
    if (!outer || !inner) return undefined;
    const fit = () => {
      inner.style.transform = '';
      const o = outer.getBoundingClientRect(), r = inner.getBoundingClientRect();
      const over = r.right - (o.right - 2);
      if (over > 0) inner.style.transform = `translateX(${-Math.ceil(over)}px)`;
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(outer); ro?.observe(inner);
    return () => ro?.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

// badge — готовый элемент (DueCount), badgeLabel — его текст для экранного диктора.
function Tab({ icon, label, active, badge, badgeLabel, onClick, C }) {
  const btnRef = useRef(null);
  const badgeRef = useRef(null);
  useKeepInside(btnRef, badgeRef, [badgeLabel]);
  return (
    <button ref={btnRef} onClick={onClick} aria-current={active ? 'page' : undefined} aria-label={badgeLabel ? `${label}. ${badgeLabel}` : label}
      style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 2px 6px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontFamily: SANS,
        color: active ? C.blueDark : C.mid, position: 'relative' }}>
      {/* Активный раздел — «таблетка» под значком (Material 3) */}
      <span style={{ position: 'relative', width: 56, maxWidth: '100%', height: 30, borderRadius: 15, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', background: active ? C.bluePale : 'transparent',
        transition: 'background .15s ease' }}>
        <Icon n={icon} s={21} sw={active ? 2.1 : 1.8} />
        {badge && <span ref={badgeRef} style={{ position: 'absolute', top: -4, left: '56%', display: 'inline-flex' }}>{badge}</span>}
      </span>
      {/* Подпись ужимается, если не влезает (испанский, пиксельный шрифт «Аркады»). */}
      <FitText size={11.5} min={9} title={label} style={{ fontWeight: active ? 600 : 500, letterSpacing: '-0.01em', textAlign: 'center' }}>{label}</FitText>
    </button>
  );
}

// «Новый» на телефоне — кнопка «+» в верхней панели: вкладок не больше пяти.
export default function BottomNav({ pal: C, active, due = { repeat: 0, fresh: 0 }, onHome, onAudio, onReview, onRead, onStats }) {
  useEffect(() => {
    const root = document.documentElement.style;
    // Панель сама занимает безопасную зону снизу, поэтому мини-плеер над ней её не добавляет.
    root.setProperty('--ac-nav-h', `calc(${NAV_H}px + env(safe-area-inset-bottom, 0px))`);
    root.setProperty('--ac-bottom-inset', '0px');
    return () => { root.removeProperty('--ac-nav-h'); root.removeProperty('--ac-bottom-inset'); };
  }, []);

  return (
    <nav aria-label={t("Разделы")} data-surface="glass" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 90,
      height: `calc(${NAV_H}px + env(safe-area-inset-bottom, 0px))`, paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      boxSizing: 'border-box', background: C.glassHdr, backdropFilter: 'saturate(1.4) blur(12px)',
      WebkitBackdropFilter: 'saturate(1.4) blur(12px)', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'stretch' }}>
      <Tab C={C} icon="home" label={t("Главная")} active={active === 'home'} onClick={onHome} />
      <Tab C={C} icon="headphones" label={t("Аудио")} active={active === 'audio'} onClick={onAudio} />
      <Tab C={C} icon="book" label={t("Чтение")} active={active === 'read'} onClick={onRead} />
      <Tab C={C} icon="cards" label={t("Повтор")} active={active === 'review'} onClick={onReview}
        badge={<DueCount C={C} repeat={due.repeat} fresh={due.fresh} variant="pill" max={99} />}
        badgeLabel={due.repeat + due.fresh > 0 ? dueLabel(due.repeat, due.fresh) : ''} />
      <Tab C={C} icon="chart" label={t("Статистика")} active={active === 'stats'} onClick={onStats} />
    </nav>
  );
}
